import { Skeleton } from "@/components/shared/Skeleton";
import { ReportDayActivityDetailDocument } from "@/generated/graphql";
import {
  type Dictionary,
  type Locale,
  format,
  formatWeekdayMonthDayLong,
  useLocale,
  useT,
} from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { HiClock, HiOutlineBookOpen, HiX } from "react-icons/hi";

interface DayActivityDetailProps {
  studentId: string;
  timezone: string;
  date: string;
  /** Whether the calendar marked this day active. Separates "no activity" from "active, but no
   * source carries a duration" — a pure review-queue day writes no practice time. */
  wasMarkedActive: boolean;
  onClose: () => void;
}

function formatDayPracticeTime(t: Dictionary, ms: number, wasMarkedActive: boolean): string {
  const totalMinutes = Math.round(ms / 60000);
  if (totalMinutes <= 0) {
    return wasMarkedActive ? t["report.day.timeUnavailable"] : t["report.day.noPractice"];
  }
  if (totalMinutes < 60) {
    return format(t["report.day.minutesPracticed"], { count: totalMinutes });
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0
    ? format(t["report.day.hoursMinutesPracticed"], { hours, minutes })
    : format(t["report.day.hoursPracticed"], { hours });
}

function formatDayTitle(locale: Locale, date: string): string {
  const [year = 1970, month = 1, day = 1] = date.split("-").map(Number);
  return formatWeekdayMonthDayLong(locale, new Date(year, month - 1, day));
}

/** What one calendar day held, opened under the grid rather than over it. */
export function DayActivityDetail({
  studentId,
  timezone,
  date,
  wasMarkedActive,
  onClose,
}: DayActivityDetailProps) {
  const t = useT();
  const locale = useLocale();
  const { data, loading } = useQuery(ReportDayActivityDetailDocument, {
    variables: { studentId, date, timezone },
  });

  const detail = data?.dayActivityDetail;
  const vocabulary = detail?.newVocabulary ?? [];

  return (
    <div className="mt-4 rounded-2xl bg-app-sky-soft p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="app-value font-bold text-app-ink">{formatDayTitle(locale, date)}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label={t["report.day.close"]}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-app-muted hover:bg-white"
        >
          <HiX className="h-4 w-4" />
        </button>
      </div>

      {loading && !detail ? (
        <Skeleton className="mt-3 h-12" />
      ) : (
        <>
          <p className="body-sm mt-3 flex items-center gap-2 text-app-text">
            <HiClock className="h-4 w-4 shrink-0 text-app-ink" />
            {formatDayPracticeTime(t, detail?.practiceTimeMs ?? 0, wasMarkedActive)}
          </p>

          <p className="app-label mt-4 flex items-center gap-1.5">
            <HiOutlineBookOpen className="h-4 w-4" />
            {t["report.day.newVocabulary"]}
          </p>
          {vocabulary.length === 0 ? (
            <p className="body-sm mt-1.5 text-app-muted">{t["report.day.noVocabulary"]}</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {vocabulary.map((item) => (
                <span
                  key={item.slideId}
                  className="rounded-full bg-white px-2.5 py-1 text-sm font-bold text-app-ink"
                >
                  {item.word}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
